import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit2, Trash2, X, Shield, UserCheck } from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from '../../layouts/AdminLayout';
import { usersApi } from '../../services/api';
import { formatDate, formatApiError, Spinner } from '../../utils/helpers';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-bold text-slate-800 font-manrope">{title}</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100"><X size={18} className="text-slate-500" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

const ROLE_COLORS = { owner: 'bg-purple-100 text-purple-700', manager: 'bg-blue-100 text-blue-700', staff: 'bg-slate-100 text-slate-600' };

export default function StaffPage() {
  const { lang, t } = useLanguage();
  const { user: me } = useAuth();
  const queryClient = useQueryClient();
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'staff', is_active: true });

  const { data: users = [], isLoading } = useQuery({ queryKey: ['users'], queryFn: () => usersApi.list().then(r => r.data) });

  const mutation = useMutation({
    mutationFn: d => modal?.mode === 'create' ? usersApi.create(d) : usersApi.update(modal.id, d),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['users'] }); setModal(null); toast.success(t('Disimpan', 'Saved')); },
    onError: err => toast.error(formatApiError(err)),
  });

  const handleDelete = async (id) => {
    if (!window.confirm(t('Hapus karyawan ini?', 'Delete this staff?'))) return;
    await usersApi.remove(id);
    queryClient.invalidateQueries({ queryKey: ['users'] });
    toast.success(t('Dihapus', 'Deleted'));
  };

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const openCreate = () => { setModal({ mode: 'create' }); setForm({ name: '', email: '', password: '', role: 'staff', is_active: true }); };
  const openEdit = (u) => { setModal({ mode: 'edit', id: u.id }); setForm({ name: u.name, email: u.email, role: u.role, is_active: u.is_active, password: '' }); };

  return (
    <AdminLayout>
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-slate-900 font-manrope">{t('Manajemen Karyawan', 'Staff Management')}</h1>
          <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2.5 bg-[#0f6c44] text-white rounded-xl text-sm font-semibold hover:bg-[#0c5636] active:scale-95 transition-all shadow-sm">
            <Plus size={18} />{t('Tambah Karyawan', 'Add Staff')}
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {isLoading ? [1,2,3].map(i => <div key={i} className="h-36 bg-slate-100 rounded-2xl animate-pulse" />) :
            users.map(u => (
              <div key={u.id} className="bg-white rounded-2xl border border-slate-100 p-5 shadow-[0_2px_10px_rgba(0,0,0,0.04)] hover:shadow-md transition-all duration-300">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                      <span className="text-[#0f6c44] font-bold">{u.name?.charAt(0)}</span>
                    </div>
                    <div>
                      <p className="font-semibold text-slate-800">{u.name}</p>
                      <p className="text-xs text-slate-400">{u.email}</p>
                    </div>
                  </div>
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${ROLE_COLORS[u.role] || ROLE_COLORS.staff}`}>{u.role}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {u.is_active ? (
                      <span className="flex items-center gap-1 text-xs text-green-600"><UserCheck size={12} />{t('Aktif', 'Active')}</span>
                    ) : (
                      <span className="text-xs text-red-400">{t('Nonaktif', 'Inactive')}</span>
                    )}
                  </div>
                  {u.id !== me?.id && (
                    <div className="flex gap-1">
                      <button onClick={() => openEdit(u)} className="p-2 text-slate-400 hover:text-[#0f6c44] hover:bg-emerald-50 rounded-lg transition-colors"><Edit2 size={15} /></button>
                      {me?.role === 'owner' && <button onClick={() => handleDelete(u.id)} className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"><Trash2 size={15} /></button>}
                    </div>
                  )}
                </div>
              </div>
            ))}
        </div>
      </div>

      {modal && (
        <Modal title={modal.mode === 'create' ? t('Tambah Karyawan', 'Add Staff') : t('Edit Karyawan', 'Edit Staff')} onClose={() => setModal(null)}>
          <div className="space-y-4">
            <div><label className="block text-sm font-medium text-slate-700 mb-1.5">{t('Nama', 'Name')} *</label>
              <input value={form.name} onChange={set('name')} required autoFocus className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-[#0f6c44]" /></div>
            <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Email{modal.mode === 'create' ? ' *' : ''}</label>
              <input type="email" value={form.email} onChange={set('email')} required={modal.mode === 'create'} disabled={modal.mode === 'edit'} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 disabled:opacity-60" /></div>
            <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Password{modal.mode === 'create' ? ' *' : t(' (kosong = tidak berubah)', ' (empty = unchanged)')}</label>
              <input type="password" value={form.password} onChange={set('password')} required={modal.mode === 'create'} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none" /></div>
            <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Role</label>
              <select value={form.role} onChange={set('role')} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none">
                <option value="staff">Staff</option>
                <option value="manager">Manager</option>
              </select></div>
            {modal.mode === 'edit' && (
              <div className="flex items-center gap-3">
                <input type="checkbox" id="is_active" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} className="w-4 h-4 accent-[#0f6c44]" />
                <label htmlFor="is_active" className="text-sm font-medium text-slate-700">{t('Akun Aktif', 'Active Account')}</label>
              </div>
            )}
            <button onClick={() => mutation.mutate(form)} disabled={mutation.isPending || !form.name}
              className="w-full h-11 bg-[#0f6c44] text-white rounded-xl font-semibold hover:bg-[#0c5636] transition-all flex items-center justify-center disabled:opacity-70">
              {mutation.isPending ? <Spinner size="sm" className="border-white/30 border-t-white" /> : t('Simpan', 'Save')}
            </button>
          </div>
        </Modal>
      )}
    </AdminLayout>
  );
}
